import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { db } from '../firebase/config';
import { 
  collection, 
  onSnapshot, 
  query, 
  where, 
  doc, 
  updateDoc,
  serverTimestamp,
  orderBy 
} from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import {
  PlayCircle,
  PauseCircle,
  CheckCircle,
  AlertCircle,
  Clock,
  Calendar,
  Users,
  Target,
  Layers,
  TrendingUp,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Edit,
  UserPlus,
  Activity,
  BarChart3,
  XCircle,
  Archive
} from 'lucide-react';
import StatusBadge, { PriorityBadge } from '../components/ui/StatusBadge';

const SopInstanceViewer = () => {
  const { instanceId } = useParams();
  const { currentUser } = useAuth();
  const { users } = useData();
  
  const [instance, setInstance] = useState(null);
  const [template, setTemplate] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activePhase, setActivePhase] = useState(null);
  const [viewMode, setViewMode] = useState('timeline'); // timeline | tasks | metrics

  // Load instance data
  useEffect(() => {
    if (!instanceId || !currentUser?.orgId) return;

    // Load instance
    const instanceUnsub = onSnapshot(
      doc(db, 'sopInstances', instanceId),
      (doc) => {
        if (doc.exists()) {
          const data = { id: doc.id, ...doc.data() };
          setInstance(data);
          
          // Load template
          if (data.templateId) {
            loadTemplate(data.templateId);
          }
        }
      }
    );

    // Load tasks
    const tasksQuery = query(
      collection(db, 'sopTasks'),
      where('orgId', '==', currentUser.orgId),
      where('instanceId', '==', instanceId),
      orderBy('dueDate', 'asc')
    );

    const tasksUnsub = onSnapshot(tasksQuery, (snapshot) => {
      const tasksList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksList);
      setLoading(false);
    });

    return () => {
      instanceUnsub();
      tasksUnsub();
    };
  }, [instanceId, currentUser?.orgId]);

  // Load template
  const loadTemplate = async (templateId) => {
    const templateDoc = await onSnapshot(
      doc(db, 'sopTemplates', templateId),
      (doc) => {
        if (doc.exists()) {
          setTemplate({ id: doc.id, ...doc.data() });
          if (!activePhase && doc.data().phases?.length > 0) {
            setActivePhase(doc.data().phases[0].id);
          }
        }
      }
    );
  };

  // Calculate progress metrics
  const calculateMetrics = () => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'done').length;
    const overdueTasks = tasks.filter(t => 
      t.status !== 'done' && new Date(t.dueDate) < new Date()
    ).length;
    const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length;

    const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

    // Phase progress
    const phaseProgress = {};
    if (template?.phases) {
      template.phases.forEach(phase => {
        const phaseTasks = tasks.filter(t => t.phaseId === phase.id);
        const phaseCompleted = phaseTasks.filter(t => t.status === 'done').length;
        phaseProgress[phase.id] = {
          total: phaseTasks.length,
          completed: phaseCompleted,
          percentage: phaseTasks.length > 0 ? (phaseCompleted / phaseTasks.length) * 100 : 0
        };
      });
    }

    return {
      totalTasks,
      completedTasks,
      overdueTasks,
      inProgressTasks,
      completionRate,
      phaseProgress
    };
  };

  const metrics = calculateMetrics();

  // Update instance status
  const updateInstanceStatus = async (newStatus) => {
    try {
      await updateDoc(doc(db, 'sopInstances', instanceId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating instance status:', error);
    }
  };

  // Update task
  const updateTask = async (taskId, updates) => {
    try {
      await updateDoc(doc(db, 'sopTasks', taskId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  // Trigger automation manually
  const triggerAutomation = async () => {
    alert('טריגר אוטומציה ידני - פיצ׳ר זה ידרוש Cloud Function');
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!instance || !template) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">לא נמצא תהליך SOP</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold">{template.name}</h1>
              <StatusBadge 
                status={instance.status === 'active' ? 'פעיל' : instance.status} 
                size="lg"
              />
            </div>
            <p className="text-gray-600">{template.description}</p>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                התחיל: {new Date(instance.startDate).toLocaleDateString('he-IL')}
              </span>
              {instance.targets.userId && (
                <span className="flex items-center gap-1">
                  <Users size={14} />
                  {users.find(u => u.id === instance.targets.userId)?.name || 'משתמש'}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {instance.status === 'active' && (
              <button
                onClick={() => updateInstanceStatus('paused')}
                className="px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
              >
                <PauseCircle size={18} />
                השהה
              </button>
            )}
            {instance.status === 'paused' && (
              <button
                onClick={() => updateInstanceStatus('active')}
                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <PlayCircle size={18} />
                חדש
              </button>
            )}
            <button
              onClick={triggerAutomation}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <RefreshCw size={18} />
              הפעל אוטומציה
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">התקדמות כללית</h3>
            <Activity size={20} className="text-blue-600" />
          </div>
          <div className="text-2xl font-bold">{metrics.completionRate.toFixed(0)}%</div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${metrics.completionRate}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">משימות הושלמו</h3>
            <CheckCircle size={20} className="text-green-600" />
          </div>
          <div className="text-2xl font-bold">{metrics.completedTasks}</div>
          <p className="text-sm text-gray-500">מתוך {metrics.totalTasks}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">בביצוע</h3>
            <Clock size={20} className="text-orange-600" />
          </div>
          <div className="text-2xl font-bold">{metrics.inProgressTasks}</div>
          <p className="text-sm text-gray-500">משימות פעילות</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">באיחור</h3>
            <AlertCircle size={20} className="text-red-600" />
          </div>
          <div className="text-2xl font-bold text-red-600">{metrics.overdueTasks}</div>
          <p className="text-sm text-gray-500">דורשות תשומת לב</p>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-4">
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setViewMode('timeline')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              viewMode === 'timeline'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            <Layers size={18} className="inline mr-1" />
            ציר זמן
          </button>
          <button
            onClick={() => setViewMode('tasks')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              viewMode === 'tasks'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            <CheckCircle size={18} className="inline mr-1" />
            משימות
          </button>
          <button
            onClick={() => setViewMode('metrics')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              viewMode === 'metrics'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
          >
            <BarChart3 size={18} className="inline mr-1" />
            מדדים
          </button>
        </div>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'timeline' && (
        <TimelineView
          template={template}
          tasks={tasks}
          metrics={metrics}
          activePhase={activePhase}
          setActivePhase={setActivePhase}
          updateTask={updateTask}
        />
      )}

      {viewMode === 'tasks' && (
        <TasksView
          tasks={tasks}
          users={users}
          updateTask={updateTask}
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
        />
      )}

      {viewMode === 'metrics' && (
        <MetricsView
          instance={instance}
          template={template}
          tasks={tasks}
          metrics={metrics}
        />
      )}
    </div>
  );
};

// Timeline View Component
const TimelineView = ({ template, tasks, metrics, activePhase, setActivePhase, updateTask }) => {
  return (
    <div className="bg-white rounded-lg shadow">
      {/* Phase Navigation */}
      <div className="border-b p-4">
        <div className="flex items-center gap-2 overflow-x-auto">
          {template.phases.map((phase, index) => {
            const isActive = phase.id === activePhase;
            const phaseMetrics = metrics.phaseProgress[phase.id];
            
            return (
              <button
                key={phase.id}
                onClick={() => setActivePhase(phase.id)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors
                  ${isActive 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                `}
              >
                <span>{phase.name}</span>
                {phaseMetrics && (
                  <span className={`text-xs ${isActive ? 'text-blue-100' : 'text-gray-500'}`}>
                    {phaseMetrics.completed}/{phaseMetrics.total}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Phase Content */}
      <div className="p-6">
        {template.phases.map(phase => {
          if (phase.id !== activePhase) return null;

          const phaseTasks = tasks.filter(t => t.phaseId === phase.id);
          const phaseMetrics = metrics.phaseProgress[phase.id];

          return (
            <div key={phase.id}>
              {/* Phase Header */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">{phase.name}</h3>
                {phase.goals?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-600 mb-1">יעדי השלב:</p>
                    <ul className="list-disc list-inside text-sm text-gray-700">
                      {phase.goals.map((goal, i) => (
                        <li key={i}>{goal}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Phase Progress */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">התקדמות בשלב</span>
                    <span className="text-sm text-gray-600">
                      {phaseMetrics?.percentage.toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${phaseMetrics?.percentage || 0}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Phase Tasks */}
              <div className="space-y-3">
                {phaseTasks.map(task => (
                  <TaskCard key={task.id} task={task} updateTask={updateTask} />
                ))}
              </div>

              {phaseTasks.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>אין משימות בשלב זה</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Tasks View Component
const TasksView = ({ tasks, users, updateTask, selectedTask, setSelectedTask }) => {
  const [filter, setFilter] = useState('all');
  
  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'overdue') return task.status !== 'done' && new Date(task.dueDate) < new Date();
    if (filter === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const taskDate = new Date(task.dueDate);
      return taskDate >= today && taskDate < tomorrow;
    }
    return task.status === filter;
  });

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-4 p-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-lg text-sm ${
              filter === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            כל המשימות ({tasks.length})
          </button>
          <button
            onClick={() => setFilter('open')}
            className={`px-3 py-1 rounded-lg text-sm ${
              filter === 'open' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            פתוחות ({tasks.filter(t => t.status === 'open').length})
          </button>
          <button
            onClick={() => setFilter('in_progress')}
            className={`px-3 py-1 rounded-lg text-sm ${
              filter === 'in_progress' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            בביצוע ({tasks.filter(t => t.status === 'in_progress').length})
          </button>
          <button
            onClick={() => setFilter('done')}
            className={`px-3 py-1 rounded-lg text-sm ${
              filter === 'done' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            הושלמו ({tasks.filter(t => t.status === 'done').length})
          </button>
          <button
            onClick={() => setFilter('overdue')}
            className={`px-3 py-1 rounded-lg text-sm ${
              filter === 'overdue' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            באיחור ({tasks.filter(t => t.status !== 'done' && new Date(t.dueDate) < new Date()).length})
          </button>
        </div>
      </div>

      {/* Tasks List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Tasks */}
        <div className="space-y-3">
          {filteredTasks.map(task => (
            <div
              key={task.id}
              onClick={() => setSelectedTask(task)}
              className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-all ${
                selectedTask?.id === task.id ? 'ring-2 ring-blue-500' : 'hover:shadow-md'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-medium">{task.title}</h4>
                  {task.description && (
                    <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                  )}
                </div>
                <PriorityBadge priority={task.priority} size="xs" />
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-3 text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users size={14} />
                    {users.find(u => u.id === task.assigneeId)?.name || 'לא מוקצה'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {new Date(task.dueDate).toLocaleDateString('he-IL')}
                  </span>
                </div>
                <StatusBadge status={task.status} size="xs" />
              </div>

              {/* Checklist Progress */}
              {task.checklist?.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>רשימת בדיקה</span>
                    <span>{task.checklist.filter(item => item.done).length}/{task.checklist.length}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div
                      className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(task.checklist.filter(item => item.done).length / task.checklist.length) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Task Details */}
        {selectedTask && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-lg mb-4">{selectedTask.title}</h3>
            
            {selectedTask.description && (
              <p className="text-gray-600 mb-4">{selectedTask.description}</p>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">סטטוס</span>
                <select
                  value={selectedTask.status}
                  onChange={(e) => updateTask(selectedTask.id, { status: e.target.value })}
                  className="px-3 py-1 border rounded-lg text-sm"
                >
                  <option value="open">פתוח</option>
                  <option value="in_progress">בביצוע</option>
                  <option value="done">הושלם</option>
                  <option value="blocked">חסום</option>
                </select>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">מוקצה ל</span>
                <select
                  value={selectedTask.assigneeId}
                  onChange={(e) => updateTask(selectedTask.id, { assigneeId: e.target.value })}
                  className="px-3 py-1 border rounded-lg text-sm"
                >
                  <option value="">לא מוקצה</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">עדיפות</span>
                <PriorityBadge priority={selectedTask.priority} />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">תאריך יעד</span>
                <span className="text-sm">{new Date(selectedTask.dueDate).toLocaleDateString('he-IL')}</span>
              </div>
            </div>

            {/* Checklist */}
            {selectedTask.checklist?.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium mb-2">רשימת בדיקה</h4>
                <div className="space-y-2">
                  {selectedTask.checklist.map((item, index) => (
                    <label key={index} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={item.done}
                        onChange={(e) => {
                          const updatedChecklist = [...selectedTask.checklist];
                          updatedChecklist[index].done = e.target.checked;
                          updateTask(selectedTask.id, { checklist: updatedChecklist });
                        }}
                        className="w-4 h-4"
                      />
                      <span className={`text-sm ${item.done ? 'line-through text-gray-500' : ''}`}>
                        {item.text}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Metrics View Component
const MetricsView = ({ instance, template, tasks, metrics }) => {
  // Calculate additional metrics
  const calculateTimeMetrics = () => {
    const startDate = new Date(instance.startDate);
    const today = new Date();
    const elapsedDays = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
    
    const completedTasks = tasks.filter(t => t.status === 'done');
    const avgCompletionTime = completedTasks.length > 0
      ? completedTasks.reduce((sum, task) => {
          const taskStart = new Date(task.createdAt);
          const taskEnd = new Date(task.updatedAt);
          return sum + (taskEnd - taskStart) / (1000 * 60 * 60 * 24);
        }, 0) / completedTasks.length
      : 0;

    const estimatedCompletion = new Date(startDate);
    estimatedCompletion.setDate(estimatedCompletion.getDate() + template.defaultDurationDays);
    const daysRemaining = Math.max(0, Math.floor((estimatedCompletion - today) / (1000 * 60 * 60 * 24)));

    return {
      elapsedDays,
      avgCompletionTime,
      daysRemaining,
      estimatedCompletion
    };
  };

  const timeMetrics = calculateTimeMetrics();

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {template.successMetrics?.map((metric, index) => {
          let currentValue = 0;
          let label = metric.kpi;
          let color = 'blue';

          if (metric.kpi === 'onboarding_completion') {
            currentValue = metrics.completionRate;
            label = 'אחוז השלמת התהליך';
            color = currentValue >= metric.target * 100 ? 'green' : 'orange';
          } else if (metric.kpi === 'time_to_productivity') {
            currentValue = timeMetrics.elapsedDays;
            label = 'ימים מתחילת התהליך';
            color = currentValue <= metric.target ? 'green' : 'orange';
          }

          return (
            <div key={index} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">{label}</h3>
                <Target size={20} className={`text-${color}-600`} />
              </div>
              <div className="text-2xl font-bold">
                {currentValue.toFixed(metric.unit === 'ratio' ? 0 : 1)}
                {metric.unit === 'ratio' ? '%' : ` ${metric.unit === 'days' ? 'ימים' : ''}`}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                יעד: {metric.target}{metric.unit === 'ratio' ? '%' : ` ${metric.unit === 'days' ? 'ימים' : ''}`}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-1 mt-2">
                <div
                  className={`bg-${color}-600 h-1 rounded-full transition-all duration-300`}
                  style={{ 
                    width: `${Math.min(100, metric.unit === 'ratio' 
                      ? currentValue 
                      : (metric.target / currentValue) * 100)}%` 
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Time Analysis */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-lg mb-4">ניתוח זמנים</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">זמן ממוצע להשלמת משימה</p>
            <p className="text-xl font-bold">{timeMetrics.avgCompletionTime.toFixed(1)} ימים</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">ימים עד סיום משוער</p>
            <p className="text-xl font-bold">{timeMetrics.daysRemaining} ימים</p>
          </div>
        </div>
      </div>

      {/* Phase Performance */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-semibold text-lg mb-4">ביצועים לפי שלב</h3>
        <div className="space-y-3">
          {template.phases.map(phase => {
            const phaseMetrics = metrics.phaseProgress[phase.id];
            return (
              <div key={phase.id} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium">{phase.name}</p>
                  <p className="text-sm text-gray-500">
                    {phaseMetrics?.completed || 0} מתוך {phaseMetrics?.total || 0} משימות הושלמו
                  </p>
                </div>
                <div className="w-48">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">{phaseMetrics?.percentage.toFixed(0) || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${phaseMetrics?.percentage || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Task Card Component
const TaskCard = ({ task, updateTask }) => {
  const isOverdue = task.status !== 'done' && new Date(task.dueDate) < new Date();
  
  return (
    <div className={`bg-white border rounded-lg p-4 ${isOverdue ? 'border-red-300' : ''}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-medium">{task.title}</h4>
          {task.description && (
            <p className="text-sm text-gray-600 mt-1">{task.description}</p>
          )}
        </div>
        <PriorityBadge priority={task.priority} size="xs" />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Clock size={14} />
            {new Date(task.dueDate).toLocaleDateString('he-IL')}
          </span>
          {isOverdue && (
            <span className="flex items-center gap-1 text-red-600">
              <AlertCircle size={14} />
              באיחור
            </span>
          )}
        </div>

        <select
          value={task.status}
          onChange={(e) => updateTask(task.id, { status: e.target.value })}
          className="px-3 py-1 border rounded-lg text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <option value="open">פתוח</option>
          <option value="in_progress">בביצוע</option>
          <option value="done">הושלם</option>
          <option value="blocked">חסום</option>
        </select>
      </div>

      {/* Checklist Preview */}
      {task.checklist?.length > 0 && (
        <div className="mt-2 pt-2 border-t">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>רשימת בדיקה</span>
            <span>{task.checklist.filter(item => item.done).length}/{task.checklist.length}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SopInstanceViewer;